import React, { useState, useCallback } from 'react'
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Toolbar,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  ListItemIcon,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Stack,
  IconButton,
  Divider,
} from '@mui/material'
import CloudUpload from '@mui/icons-material/CloudUpload'
import GetApp from '@mui/icons-material/GetApp'
import CreateNewFolder from '@mui/icons-material/CreateNewFolder'
import Delete from '@mui/icons-material/Delete'
import MoreVert from '@mui/icons-material/MoreVert'
import Sort from '@mui/icons-material/Sort'
import Search from '@mui/icons-material/Search'
import FileCopy from '@mui/icons-material/FileCopy'
import NavigateNext from '@mui/icons-material/NavigateNext'
import ViewList from '@mui/icons-material/ViewList'
import ViewModule from '@mui/icons-material/ViewModule'

import { FileIcon } from '../components/system/FileIcon'
import useAccount from '../hooks/useAccount'
import useFiles, { folderPathOf } from '../hooks/useFiles'
import useSnackbar from '../hooks/useSnackbar'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools, voiceCollection } from '../voice/buildVoiceTools'
import PageHeader from '../components/system/PageHeader'
import ActionButton from '../components/system/ActionButton'
import { useDropzone } from 'react-dropzone'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'

interface RenameFileDialog {
  open: boolean
  oldName: string
  newName: string
  folderPath: string
}

interface DeleteConfirmation {
  open: boolean
  itemToDelete: string | null
}

/** The folders an item list can move into: every folder except a moved
 *  folder itself and the folders inside it. */
export const moveTargetsFor = (folders: string[], items: string[]): string[] => {
  const moving = items.filter(i => i.endsWith('/')).map(folderPathOf)
  return folders.filter(f => !moving.some(m => f === m || f.startsWith(`${m}/`)))
}

const itemLabel = (name: string): string => {
  const path = name.replace(/^Docs\//, '')
  return path.endsWith('/') ? folderPathOf(path).split('/').pop() || path : path.split('/').pop() || path
}

const formatFileSize = (bytes: number | undefined): string => {
  if (bytes === undefined) return 'Unknown'
  const mb = bytes / (1024 * 1024)
  return mb < 0.1 ? '< 0.1 MB' : `${mb.toFixed(1)} MB`
}

export const Files: React.FC = () => {
  const account = useAccount()
  // Initialize hooks
  const files = useFiles()
  const snackbar = useSnackbar()
  
  // Local UI state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null)
  const [selectedItem, setSelectedItem] = useState<string | null>(null)
  const [createFolderDialog, setCreateFolderDialog] = useState({ open: false, folderName: '' })
  const [renameFileDialog, setRenameFileDialog] = useState<RenameFileDialog>({
    open: false,
    oldName: '',
    newName: '',
    folderPath: ''
  })
  const [renameFolderDialog, setRenameFolderDialog] = useState({ open: false, oldName: '', newName: '' })
  // items: what the dialog moves — the one item whose ⋮ menu opened it, or
  // the ticked items for the header Move. A name ending in "/" is a folder.
  const [moveFileDialog, setMoveFileDialog] = useState<{ open: boolean; items: string[]; targetFolder: string }>({ open: false, items: [], targetFolder: '' })
  const [moveDialogLoading, setMoveDialogLoading] = useState(false)
  const [moveProgress, setMoveProgress] = useState<{ total: number; completed: number } | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
    open: false,
    itemToDelete: null
  })
  const [sortConfig, setSortConfig] = useState<{ field: 'name' | 'lastModified', order: 'asc' | 'desc' }>({ 
    field: 'name', 
    order: 'asc' 
  })

  const getSortArrow = (field: 'name' | 'lastModified') => {
    if (sortConfig.field !== field) return null
    return (
      <span style={{ fontSize: '1.2rem', marginLeft: '4px' }}>
        {sortConfig.order === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const handleSort = (field: 'name' | 'lastModified') => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const sortFiles = (fileList: any[]) => {
    return fileList.sort((a, b) => {
      // Always sort folders first
      if (a.isFolder && !b.isFolder) return -1
      if (!a.isFolder && b.isFolder) return 1
      
      // Then sort by selected field
      if (sortConfig.field === 'name') {
        const comparison = a.displayName.localeCompare(b.displayName)
        return sortConfig.order === 'asc' ? comparison : -comparison
      } else {
        const dateA = a.lastModified?.getTime() || 0
        const dateB = b.lastModified?.getTime() || 0
        return sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA
      }
    })
  }

  const getSortedFiles = () => {
    return sortFiles(files.getCurrentFolderContents())
  }

  const handleContextMenu = (event: React.MouseEvent, item: string) => {
    event.preventDefault()
    setSelectedItem(item)
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    )
  }

  const handleItemClick = (event: React.MouseEvent, file: any) => {
    if ((event.target as HTMLElement).closest('.MuiIconButton-root')) {
      return
    }
    if (file.isFolder) {
      if (files.searchTerm.trim() !== '') {
        // Opens the folder that holds it, with the breadcrumb trail to match.
        files.handleFolderClick(file.parentFolder ?? '')
        files.handleSearch('')
      } else {
        files.handleFolderClick(folderPathOf(file.name))
      }
    }
  }

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    if (!fileList || !fileList.length) return
    
    await files.uploadMultipleFiles(fileList)
  }

  const handleCreateFolder = async () => {
    if (!createFolderDialog.folderName) return
    
    await files.createFolder(createFolderDialog.folderName)
    setCreateFolderDialog({ open: false, folderName: '' })
  }

  const handleRenameFile = async () => {
    if (!renameFileDialog.newName) return
    
    await files.renameFile(renameFileDialog.oldName, renameFileDialog.newName, renameFileDialog.folderPath)
    setRenameFileDialog({ open: false, oldName: '', newName: '', folderPath: '' })
  }

  const handleRenameFolder = async () => {
    if (!renameFolderDialog.newName) return
    
    await files.renameFolder(renameFolderDialog.oldName, renameFolderDialog.newName)
    setRenameFolderDialog({ open: false, oldName: '', newName: '' })
  }

  const closeMoveDialog = () => {
    setMoveProgress(null)
    setMoveFileDialog({ open: false, items: [], targetFolder: '' })
  }

  /** Moves every item in the dialog, one at a time. Success is said only when
   *  every item moved; otherwise each failure is named with its reason and
   *  the dialog stays open on what did not move. */
  const handleMove = async () => {
    const { items, targetFolder } = moveFileDialog
    if (items.length === 0) return
    const where = targetFolder === '' ? 'Root' : targetFolder
    setMoveDialogLoading(true)
    setMoveProgress(items.length > 1 ? { total: items.length, completed: 0 } : null)
    const failed: { item: string; reason: string }[] = []
    try {
      for (const item of items) {
        const reason = await files.moveItem(item, targetFolder)
        if (reason) failed.push({ item, reason })
        setMoveProgress(prev => prev ? { ...prev, completed: Math.min(prev.completed + 1, prev.total) } : null)
      }
      await files.fetchFiles()
      files.clearSelection()
      if (failed.length === 0) {
        snackbar.success(items.length === 1 ? `Moved ${itemLabel(items[0])} to ${where}` : `Moved ${items.length} items to ${where}`)
        closeMoveDialog()
      } else {
        const first = `${itemLabel(failed[0].item)}: ${failed[0].reason}`
        snackbar.error(items.length === 1
          ? `Could not move ${first}`
          : `${failed.length} of ${items.length} items were not moved. ${first}${failed.length > 1 ? ` (and ${failed.length - 1} more)` : ''}`)
        setMoveFileDialog(prev => ({ ...prev, items: failed.map(f => f.item) }))
      }
    } finally {
      setMoveDialogLoading(false)
      setMoveProgress(null)
    }
  }

  const handleDelete = (item: string) => {
    setDeleteConfirmation({
      open: true,
      itemToDelete: item
    })
  }

  const confirmDelete = async () => {
    if (!deleteConfirmation.itemToDelete) return
    
    const item = deleteConfirmation.itemToDelete
    
    if (item.endsWith('/')) {
      await files.deleteFolder(item.replace('Docs/', '').slice(0, -1))
    } else {
      await files.deleteFile(item.replace('Docs/', ''))
    }
    
    files.clearSelection()
    setDeleteConfirmation({ open: false, itemToDelete: null })
  }

  const handleBulkDownload = async () => {
    await files.downloadItems(Array.from(files.selectedFiles))
  }

  const handleBulkDelete = async () => {
    for (const item of Array.from(files.selectedFiles)) {
      try {
        if (item.endsWith('/')) {
          await files.deleteFolder(item.replace('Docs/', '').slice(0, -1))
        } else {
          await files.deleteFile(item.replace('Docs/', ''))
        }
      } catch (error) {
        console.error(`Failed to delete ${item}:`, error)
      }
    }
    files.clearSelection()
    setDeleteConfirmation({ open: false, itemToDelete: null })
  }

  const getFirstSelectedFile = (): string | undefined => Array.from(files.selectedFiles)[0]

  // The sub-folders of the open folder, by full path.
  const subFolders = files.files
    .filter(f => f.isFolder && (f.parentFolder ?? null) === files.currentFolder)
    .map(f => folderPathOf(f.name))

  // Drag and drop functionality
  const onDropFiles = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(file => {
      files.uploadFile(file)
    })
  }, [files])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropFiles,
    noClick: true,
    noKeyboard: true
  })

  // The Files helper (Douglas plan T14).
  const [helperOpen, setHelperOpen] = useState(false)

  useVoiceRegistration({
    // The folder is part of the key: tools are snapshotted per key, and the
    // open_folder options and every `files.*` closure must follow the folder.
    pageKey: `files:${account.customer?.name || ''}:${account.job?.name || ''}:${files.breadcrumbs.join('/')}`,
    pageContext: () =>
      `The file area for ${account.customer?.name || ''}/${account.job?.name || ''}. You can search files, switch view, download the current selection, or find a file by name and download it directly.`,
    uiTools: buildVoiceTools({
      fields: [
        { name: 'search_files', label: 'the file search', set: (v) => files.handleSearch(v) },
      ],
      choices: [
        {
          name: 'set_files_view',
          label: 'the file view',
          options: [
            { value: 'grid', describe: 'card grid layout' },
            { value: 'list', describe: 'sortable table/list layout' },
          ],
          select: (v) => setViewMode(v as 'grid' | 'list'),
        },
        {
          name: 'open_folder',
          label: 'the folder to open (a sub-folder of the current one)',
          options: subFolders,
          optionsFrom: 'read_current_folder',
          select: (v) => { files.handleFolderClick(v); return `opened ${v}` },
          enabled: () => subFolders.length > 0,
        },
      ],
      reads: [
        { name: 'read_current_folder', label: 'the current folder path, its sub-folders and how many files are selected', get: () => JSON.stringify({ path: files.breadcrumbs.join('/') || '/', folders: subFolders, selected: files.selectedFiles.size }) },
      ],
      actions: [
        { name: 'go_to_root', label: 'go back to the top-level folder', run: () => files.handleBreadcrumbClick(0), enabled: () => files.breadcrumbs.length > 1 },
        { name: 'go_up', label: 'go up one folder', run: () => files.handleBreadcrumbClick(Math.max(0, files.breadcrumbs.length - 2)), enabled: () => files.breadcrumbs.length > 1 },
        { name: 'clear_selection', label: 'clear the file selection', run: () => files.handleSelectAll(false), enabled: () => files.selectedFiles.size > 0 },
        {
          name: 'download_selected_files',
          label: 'download the selected files',
          enabled: () => files.selectedFiles.size > 0,
          run: () => handleBulkDownload(),
        },
      ],
      collections: [
        voiceCollection({
          name: 'file',
          label: 'file',
          list: () => files.files.filter((f) => !f.isFolder),
          identify: (f) => ({ id: f.name, label: f.displayName || f.name, keywords: f.parentFolder ?? '' }),
          actions: [
            { name: 'download_file', label: 'download', run: (f) => files.downloadFile(f.name) },
            { name: 'select_file', label: 'select (add to the selection)', run: (f) => files.handleSelectFile(f.name) },
          ],
        }),
      ],
    }),
  })

  if (!account.customer || !account.job) {
    return (
      <Box sx={{ 
        display: 'flex', 
        height: 'calc(100vh - 64px)', 
        overflow: 'hidden', 
        pl: 2, 
        pr: 2,
        py: 2,
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Select Customer and Job
          </Typography>
          <Typography>Please select a customer and job to view files and folders.</Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{
      display: 'flex',
      height: 'calc(100vh - 64px)',
      overflow: 'hidden',
      flexDirection: 'column'
    }}>
      <PageHeader
        title="Files"
        actions={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <PageAgentButton
              pageKey="files"
              customer={account.customer.name || ''}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
            <Button
              component="label"
              variant="text"
              startIcon={<CloudUpload fontSize="small" />}
              sx={{
                fontSize: '0.8125rem',
                px: 1.5,
                py: 0.75,
                borderRadius: '6px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
              }}
            >
              Upload
              <input
                type="file"
                hidden
                multiple
                onChange={handleUpload}
                onClick={(e) => { (e.target as HTMLInputElement).value = '' }}
              />
            </Button>
            <ActionButton icon={<CreateNewFolder />} label="New Folder" onClick={() => setCreateFolderDialog({ open: true, folderName: '' })} />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
            <ActionButton icon={<GetApp />} label={`Download${files.selectedFiles.size > 0 ? ` (${files.selectedFiles.size})` : ''}`} onClick={handleBulkDownload} />
            <ActionButton icon={<FileCopy />} label="Move" onClick={() => {
              setMoveProgress(null)
              setMoveFileDialog({ open: true, items: Array.from(files.selectedFiles), targetFolder: '' })
            }} />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
            <ActionButton icon={<Delete />} label="Delete" onClick={() => {
              if (files.selectedFiles.size > 1) {
                setDeleteConfirmation({ open: true, itemToDelete: 'MULTI' })
              } else {
                const firstFile = getFirstSelectedFile()
                if (firstFile) handleDelete(firstFile)
              }
            }} />
          </Box>
        }
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {files.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Breadcrumbs, Actions, Search, and View Toggle */}
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', gap: 1 }}>
              <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb" sx={{ mr: 'auto' }}>
                {files.breadcrumbs.map((crumb, index) => (
                  <Typography
                    key={index}
                    color={index === files.breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => files.handleBreadcrumbClick(index)}
                  >
                    {crumb}
                  </Typography>
                ))}
              </Breadcrumbs>
              <TextField
                size="small"
                placeholder="Search files..."
                value={files.searchTerm}
                onChange={(e) => files.handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                }}
                sx={{ width: 280 }}
              />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Grid View">
                  <IconButton
                    aria-label="Grid View"
                    size="small"
                    onClick={() => setViewMode('grid')}
                    sx={{
                      p: 0.5,
                      border: '1px solid',
                      borderColor: viewMode === 'grid' ? 'primary.main' : 'divider',
                      borderRadius: '50%',
                      color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                      backgroundColor: viewMode === 'grid' ? 'rgba(30, 45, 77, 0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' },
                    }}
                  >
                    <ViewModule sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="List View">
                  <IconButton
                    aria-label="List View"
                    size="small"
                    onClick={() => setViewMode('list')}
                    sx={{
                      p: 0.5,
                      border: '1px solid',
                      borderColor: viewMode === 'list' ? 'primary.main' : 'divider',
                      borderRadius: '50%',
                      color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                      backgroundColor: viewMode === 'list' ? 'rgba(30, 45, 77, 0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' },
                    }}
                  >
                    <ViewList sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* File Content Area */}
            <Box 
              {...getRootProps()}
              sx={{ 
                position: 'relative',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.paper',
                ...(isDragActive && {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                  backgroundColor: 'action.hover'
                })
              }}
            >
              <input {...getInputProps()} />
              {viewMode === 'grid' ? (
                // Grid View
                getSortedFiles().length === 0 ? (
                  files.searchTerm.trim() !== '' ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No files found matching your search
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      py: 4,
                      px: 3,
                      textAlign: 'center'
                    }}>
                      <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {files.currentFolder ? 'This folder is empty' : 'Drop files here or use the buttons above'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Button
                          component="label"
                          variant="text"
                          startIcon={<CloudUpload fontSize="small" />}
                          sx={{
                            fontSize: '0.8125rem',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '6px',
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
                          }}
                        >
                          Upload Files
                          <input type="file" hidden multiple onChange={handleUpload} onClick={(e) => { (e.target as HTMLInputElement).value = '' }} />
                        </Button>
                        <ActionButton icon={<CreateNewFolder />} label="New Folder" onClick={() => setCreateFolderDialog({ open: true, folderName: '' })} />
                      </Box>
                    </Box>
                  )
                ) : (
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    {getSortedFiles().map((file) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={file.name}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: file.isFolder ? 'pointer' : 'default',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 3
                            },
                            border: files.selectedFiles.has(file.name) ? '2px solid' : '1px solid',
                            borderColor: files.selectedFiles.has(file.name) ? 'primary.main' : 'divider'
                          }}
                          onClick={(event) => handleItemClick(event, file)}
                        >
                          <CardContent sx={{ flexGrow: 1, textAlign: 'center', pb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Checkbox
                                checked={files.selectedFiles.has(file.name)}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  files.handleSelectFile(file.name)
                                }}
                                inputProps={{ 'aria-label': `Select ${file.displayName}` }}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleContextMenu(event, file.name)
                                }}
                                aria-label={`Actions for ${file.displayName}`}
                                sx={{ p: 0.5 }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                              <FileIcon 
                                filename={file.displayName} 
                                isFolder={file.isFolder} 
                                isOpen={false} 
                                size={48} 
                              />
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'medium',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                mb: 1
                              }}
                            >
                              {files.searchTerm.trim() !== '' ? file.name : file.displayName}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'block', textAlign: 'center' }}
                            >
                              {file.lastModified?.toLocaleDateString() ?? 'Unknown'}
                            </Typography>
                            {!file.isFolder && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ display: 'block', textAlign: 'center' }}
                              >
                                {formatFileSize(file.size)}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : (
                // List View (Table)
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: 'background.paper', fontWeight: 'bold' } }}>
                        <TableCell padding="checkbox" sx={{ width: '48px' }}>
                          <Checkbox
                            checked={files.isAllSelected()}
                            onChange={(e) => files.handleSelectAll(e.target.checked)}
                            indeterminate={files.isIndeterminate()}
                            inputProps={{ 'aria-label': 'Select all files' }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: '40%' }}>
                          <Button
                            onClick={() => handleSort('name')}
                            startIcon={<Sort fontSize="small" sx={{ color: 'grey.500' }} />}
                            color={sortConfig.field === 'name' ? 'info' : 'inherit'}
                            size="small"
                            sx={{
                              minHeight: '28px',
                              fontSize: '0.75rem',
                              px: 1.5,
                              py: 0.5
                            }}
                          >
                            Name {getSortArrow('name')}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ width: '80px' }}>Actions</TableCell>
                        <TableCell sx={{ width: '30%' }}>
                          <Button
                            onClick={() => handleSort('lastModified')}
                            startIcon={<Sort fontSize="small" sx={{ color: 'grey.500' }} />}
                            color={sortConfig.field === 'lastModified' ? 'info' : 'inherit'}
                            size="small"
                            sx={{
                              minHeight: '28px',
                              fontSize: '0.75rem',
                              px: 1.5,
                              py: 0.5
                            }}
                          >
                            Modified {getSortArrow('lastModified')}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ width: '100px' }}>Size</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedFiles().length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ border: 'none', py: 8 }}>
                            {files.searchTerm.trim() !== '' ? (
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                  No files found matching your search
                                </Typography>
                              </Box>
                            ) : (
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                textAlign: 'center'
                              }}>
                                <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {files.currentFolder ? 'This folder is empty' : 'Drop files here or use the buttons above'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Button
                                    component="label"
                                    variant="text"
                                    startIcon={<CloudUpload fontSize="small" />}
                                    sx={{
                                      fontSize: '0.8125rem',
                                      px: 1.5,
                                      py: 0.75,
                                      borderRadius: '6px',
                                      color: 'text.secondary',
                                      '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
                                    }}
                                  >
                                    Upload Files
                                    <input type="file" hidden multiple onChange={handleUpload} onClick={(e) => { (e.target as HTMLInputElement).value = '' }} />
                                  </Button>
                                  <ActionButton icon={<CreateNewFolder />} label="New Folder" onClick={() => setCreateFolderDialog({ open: true, folderName: '' })} />
                                </Box>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        getSortedFiles().map((file) => (
                          <TableRow
                            key={file.name}
                            hover
                            onClick={(event) => handleItemClick(event, file)}
                            style={{ cursor: file.isFolder ? 'pointer' : 'default' }}
                          >
                            <TableCell padding="checkbox" sx={{ width: '48px' }}>
                              <Checkbox
                                checked={files.selectedFiles.has(file.name)}
                                onChange={(e) => files.handleSelectFile(file.name)}
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ 'aria-label': `Select ${file.displayName}` }}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '40%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', height: 24 }}>
                                  <FileIcon filename={file.displayName} isFolder={file.isFolder} isOpen={false} size={24} />
                                </Box>
                                {files.searchTerm.trim() !== '' ? file.name : file.displayName}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: '80px' }}>
                              <IconButton
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleContextMenu(event, file.name)
                                }}
                                aria-label={`Actions for ${file.displayName}`}
                              >
                                <MoreVert />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ width: '30%' }}>{file.lastModified?.toLocaleString() ?? 'Unknown'}</TableCell>
                            <TableCell sx={{ width: '100px' }}>{file.isFolder ? '--' : formatFileSize(file.size)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {!selectedItem?.endsWith('/') && (
          <MenuItem onClick={() => {
            setContextMenu(null)
            files.downloadFile(selectedItem!.replace('Docs/', ''))
          }}>Download</MenuItem>
        )}
        <MenuItem onClick={() => {
          setContextMenu(null)
          setMoveProgress(null)
          setMoveFileDialog({ open: true, items: [selectedItem!], targetFolder: '' })
        }}>Move</MenuItem>
        {selectedItem?.endsWith('/') ? (
          <MenuItem onClick={() => {
            setContextMenu(null)
            setRenameFolderDialog({ open: true, oldName: selectedItem!.replace('Docs/', '').slice(0, -1), newName: '' })
          }}>Rename</MenuItem>
        ) : (
          <MenuItem onClick={() => {
            setContextMenu(null)
            const fileName = selectedItem!.split('/').pop() || ''
            const folderPath = selectedItem!.substring(0, selectedItem!.lastIndexOf('/'))
            setRenameFileDialog({ 
              open: true, 
              oldName: selectedItem!, 
              newName: fileName,
              folderPath
            })
          }}>Rename</MenuItem>
        )}
        <MenuItem onClick={() => {
          setContextMenu(null)
          handleDelete(selectedItem!)
        }}>Delete</MenuItem>
        {selectedItem?.endsWith('/') && (
          <MenuItem onClick={() => {
            files.downloadFolder(selectedItem!)
            setContextMenu(null)
          }}>
            <ListItemIcon>
              <GetApp fontSize="small" />
            </ListItemIcon>
            Download as ZIP
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <Dialog open={createFolderDialog.open} onClose={() => setCreateFolderDialog({ open: false, folderName: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={createFolderDialog.folderName}
            onChange={(e) => setCreateFolderDialog({ ...createFolderDialog, folderName: e.target.value })}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog({ open: false, folderName: '' })}>Cancel</Button>
          {/* data-voice="confirm": Douglas asks before pressing (#1363) — writes a new folder to Azure blob storage */}
          <Button onClick={handleCreateFolder} variant="contained" disabled={!createFolderDialog.folderName} data-voice="confirm">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameFileDialog.open} onClose={() => setRenameFileDialog({ open: false, oldName: '', newName: '', folderPath: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Rename File</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New File Name"
            fullWidth
            value={renameFileDialog.newName}
            onChange={(e) => setRenameFileDialog({ ...renameFileDialog, newName: e.target.value })}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFileDialog({ open: false, oldName: '', newName: '', folderPath: '' })}>Cancel</Button>
          <Button onClick={handleRenameFile} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameFolderDialog.open} onClose={() => setRenameFolderDialog({ open: false, oldName: '', newName: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Folder Name"
            fullWidth
            value={renameFolderDialog.newName}
            onChange={(e) => setRenameFolderDialog({ ...renameFolderDialog, newName: e.target.value })}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFolderDialog({ open: false, oldName: '', newName: '' })}>Cancel</Button>
          <Button onClick={handleRenameFolder} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveFileDialog.open} onClose={closeMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {moveFileDialog.items.length > 1
            ? 'Move Items'
            : moveFileDialog.items[0]?.endsWith('/') ? 'Move Folder' : 'Move File'}
        </DialogTitle>
        <DialogContent>
          {moveFileDialog.items.length === 0 ? (
            <Typography variant="body2" gutterBottom>
              Tick the files or folders to move first.
            </Typography>
          ) : moveFileDialog.items.length === 1 ? (
            <Typography variant="body1" gutterBottom>
              Moving: {moveFileDialog.items[0].replace('Docs/', '')}
            </Typography>
          ) : (
            <Typography variant="body2" gutterBottom>
              Moving {moveFileDialog.items.length} items
            </Typography>
          )}
          {moveFileDialog.items.some(i => i.endsWith('/')) && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              A folder moves with everything in it.
            </Typography>
          )}
          {moveDialogLoading && moveProgress && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={Math.round((moveProgress.completed / Math.max(moveProgress.total, 1)) * 100)} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {moveProgress.completed} / {moveProgress.total} moved
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Target folder
          </Typography>
          <FormControl fullWidth margin="dense" size="small">
            <Select
              value={moveFileDialog.targetFolder}
              onChange={(e) => setMoveFileDialog({ ...moveFileDialog, targetFolder: e.target.value as string })}
              displayEmpty
              renderValue={(value) => (value === '' ? 'Root' : String(value))}
              inputProps={{ 'aria-label': 'Target folder' }}
            >
              <MenuItem value="">
                <em>Root</em>
              </MenuItem>
              {moveTargetsFor(files.folders, moveFileDialog.items).map((folder) => (
                <MenuItem key={folder} value={folder}>{folder}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog}>Cancel</Button>
          <Button onClick={handleMove} variant="contained" disabled={moveDialogLoading || moveFileDialog.items.length === 0}>
            {moveDialogLoading ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, itemToDelete: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {deleteConfirmation.itemToDelete === 'MULTI' ? (
            <>
              <Typography>
                Are you sure you want to delete {files.selectedFiles.size} items?
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                Are you sure you want to delete this {deleteConfirmation.itemToDelete?.endsWith('/') ? 'folder' : 'file'}?
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1 }}>
                {deleteConfirmation.itemToDelete?.replace('Docs/', '').endsWith('/') 
                  ? deleteConfirmation.itemToDelete.replace('Docs/', '').slice(0, -1) 
                  : deleteConfirmation.itemToDelete?.replace('Docs/', '')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, itemToDelete: null })}>Cancel</Button>
          <Button onClick={deleteConfirmation.itemToDelete === 'MULTI' ? handleBulkDelete : confirmDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <PageAgentDrawer
        pageKey="files"
        customer={account.customer.name || ''}
        job={account.job.name || ''}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default Files